import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { useToast } from "@/components/Toaster";
import { Facts, Problem } from "@/components/PreviewBits";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import type { DeliveryStep } from "./delivery";

// Lulu print jobs that were rejected or failed (crm-billing: fulfillments_update_address, fulfillments_retry).

export interface ShipAddress {
  name?: string;
  line1?: string;
  line2?: string;
  city?: string;
  region?: string;
  postal_code?: string;
  country_code?: string;
  phone?: string;
}

export interface PrintJob {
  id: number;
  order_id: number;
  status: string;
  shipping_address: ShipAddress | null;
  error: string | null;
  external_id: string | null;
  submitted_at: string | null;
  updated_at: string;
}

/** Print jobs Lulu hasn't taken: waiting to be sent, or failed or rejected. Only these can be fixed and resent. */
export const fixablePrintJob = (status: string) => status === "error" || status === "pending";

const FIELDS: [keyof ShipAddress, string, string][] = [
  ["name", "Name", "Pat Example"],
  ["line1", "Street address", "123 Main St"],
  ["line2", "Apartment or suite", ""],
  ["city", "City", "Gunnison"],
  ["region", "State", "CO"],
  ["postal_code", "ZIP", "81230"],
  ["country_code", "Country (2 letters)", "US"],
  ["phone", "Phone (Lulu requires one)", "970-555-0100"],
];

/** Only the fields that changed (the server keeps the rest); clearing apartment/suite needs clear_line2. */
export function addressChanges(before: ShipAddress, after: ShipAddress): Record<string, string | boolean> {
  const out: Record<string, string | boolean> = {};
  for (const [k] of FIELDS) {
    const a = (before[k] ?? "").trim();
    const b = (after[k] ?? "").trim();
    if (a === b) continue;
    if (k === "line2" && !b) out.clear_line2 = true;
    else if (b) out[k] = b;
  }
  return out;
}

export const addressText = (a: ShipAddress | null | undefined) =>
  a ? [a.name, a.line1, a.line2, [a.city, [a.region, a.postal_code].filter(Boolean).join(" ")].filter(Boolean).join(", "), a.country_code, a.phone].filter(Boolean).join(", ") : "";

/** Buttons beside a print step in the delivery checklist (shown when the step isn't done or under way). */
export function PrintJobActions({ step, orderId }: { step: DeliveryStep; orderId: number }) {
  const [open, setOpen] = useState<"address" | "resend" | null>(null);
  if (step.kind !== "print" || step.ref_type !== "fulfillment" || !step.ref_id) return null;
  if (step.state === "done" || step.state === "processing") return null;
  return (
    <span className="flex flex-wrap gap-1">
      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setOpen("address")}>
        Fix shipping address
      </Button>
      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setOpen("resend")}>
        Resend to Lulu
      </Button>
      {open === "address" && <FixAddressDialog jobId={step.ref_id} orderId={orderId} onClose={() => setOpen(null)} onResend={() => setOpen("resend")} />}
      {open === "resend" && <ResendDialog jobId={step.ref_id} orderId={orderId} onClose={() => setOpen(null)} />}
    </span>
  );
}

function usePrintJob(jobId: number, orderId: number) {
  return useQuery({
    queryKey: ["print-jobs", orderId],
    queryFn: () => api<{ fulfillments: PrintJob[] }>("/api/v1/fulfillments" + qs({ order_id: orderId, limit: 50 })).then((r) => r.fulfillments),
    select: (jobs) => jobs.find((j) => j.id === jobId) ?? null,
  });
}

function useRefresh(orderId: number) {
  const qc = useQueryClient();
  return () => {
    for (const k of [["print-jobs", orderId], ["order-delivery", orderId], ["delivery-problems"], ["order", orderId]]) void qc.invalidateQueries({ queryKey: k });
  };
}

function JobState({ job }: { job: PrintJob }) {
  return (
    <Facts
      rows={[
        ["Print job", `#${job.id}${job.external_id ? ` (Lulu ${job.external_id})` : ""}`],
        ["Status", job.status === "error" ? "failed or rejected by Lulu" : job.status === "pending" ? "waiting to be sent" : job.status.replace(/_/g, " ")],
        ["Lulu said", job.error ?? undefined],
        ["Ships to", addressText(job.shipping_address) || "no address"],
        ["Last change", formatDateTime(job.updated_at)],
      ]}
    />
  );
}

/** fulfillments_update_address: correct the address of a waiting or rejected job; nothing is sent. */
export function FixAddressDialog({ jobId, orderId, onClose, onResend }: { jobId: number; orderId: number; onClose: () => void; onResend: () => void }) {
  const job = usePrintJob(jobId, orderId);
  const refresh = useRefresh(orderId);
  const toast = useToast();
  const [form, setForm] = useState<ShipAddress | null>(null);
  const [review, setReview] = useState(false);
  const [problem, setProblem] = useState("");
  const before = job.data?.shipping_address ?? {};
  const cur = form ?? before;
  const changes = addressChanges(before, cur);
  const save = useMutation({
    mutationFn: () => api(`/api/v1/fulfillments/${jobId}/address`, { method: "PATCH", json: changes }),
    onMutate: () => setProblem(""),
    onSuccess: () => {
      refresh();
      toast(`Print job #${jobId}: address saved`);
      onResend();
    },
    onError: (e) => {
      setProblem(e.message);
      setReview(false);
    },
  });
  const fixable = job.data ? fixablePrintJob(job.data.status) : false;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(600px,calc(100vw-2rem))]">
        <DialogTitle>{review ? "Check the new address" : `Fix the shipping address on print job #${jobId}`}</DialogTitle>
        <DialogDescription>
          Confirm the address with the customer first. Saving changes only this print job (the order keeps what the buyer typed) and sends nothing; you resend it to Lulu next.
        </DialogDescription>
        {job.isLoading && <Skeleton className="h-24" />}
        {job.error && <Problem text={`Couldn't load the print job: ${job.error.message}`} />}
        {job.data === null && <Problem text={`Print job #${jobId} isn't on this order.`} />}
        {job.data && !fixable && <Problem text={`Print job #${jobId} is ${job.data.status.replace(/_/g, " ")}: Lulu already has it, so its address can't change here. Change it in Lulu's dashboard.`} />}
        {job.data && fixable && !review && (
          <>
            {job.data.error && <p className="text-sm text-[var(--color-danger-fg)]">Lulu said: {job.data.error}</p>}
            <div className="grid gap-3 text-sm sm:grid-cols-2">
              {FIELDS.map(([k, label, ph]) => (
                <div key={k} className={k === "line1" || k === "name" ? "space-y-1 sm:col-span-2" : "space-y-1"}>
                  <Label htmlFor={`fa-${k}`}>{label}</Label>
                  <Input id={`fa-${k}`} value={cur[k] ?? ""} placeholder={ph} onChange={(e) => setForm({ ...cur, [k]: e.target.value })} />
                </div>
              ))}
            </div>
          </>
        )}
        {job.data && fixable && review && (
          <Facts
            rows={[
              ["Was", addressText(before) || "no address"],
              ["Will be", addressText(cur)],
            ]}
          />
        )}
        <Problem text={problem} />
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={review ? () => setReview(false) : onClose}>
            {review ? "Back" : "Cancel"}
          </Button>
          {fixable &&
            (!review ? (
              <Button disabled={Object.keys(changes).length === 0} onClick={() => setReview(true)}>
                Review
              </Button>
            ) : (
              <Button disabled={save.isPending} onClick={() => save.mutate()}>
                Save the address
              </Button>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** fulfillments_retry: sends the job to Lulu again. Lulu charges per book, so it asks first. */
export function ResendDialog({ jobId, orderId, onClose }: { jobId: number; orderId: number; onClose: () => void }) {
  const job = usePrintJob(jobId, orderId);
  const refresh = useRefresh(orderId);
  const toast = useToast();
  const [problem, setProblem] = useState("");
  const send = useMutation({
    mutationFn: () => api(`/api/v1/fulfillments/${jobId}/retry`, { json: {} }),
    onMutate: () => setProblem(""),
    onSuccess: () => {
      refresh();
      toast(`Print job #${jobId} queued for Lulu`);
      onClose();
    },
    onError: (e) => setProblem(e.message),
  });
  const fixable = job.data ? fixablePrintJob(job.data.status) : false;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(560px,calc(100vw-2rem))]">
        <DialogTitle>Resend print job #{jobId} to Lulu?</DialogTitle>
        <DialogDescription>
          Lulu prints and ships it within its usual time and charges the Lulu account per book. Check Lulu's dashboard first that this order isn't already there, so a book isn't
          printed twice.
        </DialogDescription>
        {job.isLoading && <Skeleton className="h-24" />}
        {job.error && <Problem text={`Couldn't load the print job: ${job.error.message}`} />}
        {job.data && <JobState job={job.data} />}
        {job.data && !fixable && <Problem text={`Print job #${jobId} is ${job.data.status.replace(/_/g, " ")}: only a failed or waiting job can be resent.`} />}
        <Problem text={problem} />
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Not now
          </Button>
          <Button variant="destructive" disabled={!fixable || send.isPending} onClick={() => send.mutate()}>
            Resend to Lulu
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
